import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ShieldCheck, MapPin, FileStack } from "lucide-react";
import { scrollToId } from "../../lib/scroll";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["Uw Accijnsadministratie", "Waterdicht Geregeld"];

const BADGES = [
  { icon: ShieldCheck, label: "100% Compliance" },
  { icon: MapPin, label: "Belgische Accijns Expertise" },
  { icon: FileStack, label: "EMCS & NEW AC4 Support" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-[74px]"
    >
      {/* background dot grid + amber glow */}
      <div className="pointer-events-none absolute inset-0 dot-grid opacity-70" />
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#F59E0B]/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 top-1/2 h-[420px] w-[420px] rounded-full bg-[#0F172A]/5 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-24 pt-16 sm:px-8 lg:grid-cols-12 lg:pb-32 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 backdrop-blur"
          >
            <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
            Belgische Accijns Consultancy
          </motion.div>

          <h1 className="font-display text-[2.6rem] font-extrabold leading-[1.02] tracking-tighter text-[#0F172A] sm:text-6xl lg:text-[4.6rem]">
            {HEADLINE.map((ln, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                >
                  {i === 1 ? (
                    <>
                      Waterdicht{" "}
                      <span className="relative inline-block text-[#F59E0B]">
                        Geregeld
                        <svg
                          className="absolute -bottom-2 left-0 h-3 w-full text-[#F59E0B]"
                          viewBox="0 0 300 12"
                          fill="none"
                          preserveAspectRatio="none"
                        >
                          <motion.path
                            d="M2 9C60 3 120 3 180 6C230 8 270 9 298 4"
                            stroke="currentColor"
                            strokeWidth="4"
                            strokeLinecap="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 1, delay: 1.1, ease: "easeInOut" }}
                          />
                        </svg>
                      </span>
                    </>
                  ) : (
                    ln
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-slate-600"
          >
            Zorgeloos produceren, vervoeren, opslaan, in- en uitvoeren — terwijl
            wij de accijnscomplexiteit voor u vereenvoudigen.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToId("#contact")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-7 py-3.5 text-sm font-bold text-[#0F172A] shadow-[0_12px_28px_rgba(245,158,11,0.35)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#f0a92a]"
            >
              Vraag een Accijns Quickscan Aan
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("#diensten")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-bold text-[#0F172A] transition-colors duration-200 hover:border-[#0F172A]"
            >
              Onze Diensten
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {BADGES.map((b) => (
              <div
                key={b.label}
                className="flex items-center gap-2 text-sm font-semibold text-slate-600"
              >
                <b.icon className="h-4.5 w-4.5 text-[#0F172A]" strokeWidth={2} />
                {b.label}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Framed spotlight image */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-[#0F172A]/5 to-[#F59E0B]/10 blur-xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-slate-200 shadow-[0_30px_60px_rgba(15,23,42,0.18)]">
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src="https://images.unsplash.com/photo-1651171052773-1f46b5894d6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Brouwerij met accijnsgoederen onder schorsing"
                className="h-[440px] w-full object-cover lg:h-[540px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-md">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                  Onder schorsing
                </p>
                <p className="mt-1 font-display text-lg font-bold text-white">
                  Erkend Entrepothouder statuut
                </p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="absolute -left-6 top-10 hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.12)] sm:block"
            >
              <p className="font-display text-2xl font-extrabold text-[#0F172A]">
                €0
              </p>
              <p className="text-xs font-semibold text-slate-500">
                boetes bij correcte flow
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
